import * as React from 'react';
import styled from 'styled-components';
import { AncestorIdData, AncestorNameData, FolderId } from 'modules/domains/foldering';
import { FolderClosed } from 'ui/icons/action/FolderClosed';
import { FolderOpen } from 'ui/icons/action/FolderOpen';
import { ChevronRightSmall } from 'ui/icons/navigation/ChevronRightSmall';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { body2, spacing } from 'ui/theme';
import { fontSizes } from 'ui/theme/typography';
import { FolderName } from './FolderName';
import { SegmentWrapper } from '../Folders.styles';

export const StyledEllipsis = styled.span`
  ${body2}
  font-size: ${fontSizes.s12};
  line-height: ${fontSizes.s16};
  margin-left: ${spacing.half};
`;

export type Props = {
  /** the id and name of the folder */
  data: AncestorIdData & AncestorNameData;
  /** the index of the current folder within the path array */
  folderIndex: number;
  /** optional override for the name, useful for "All" override */
  nameOverride?: string;
  /** Function run when a new folder is selected */
  onSelect: (folderId: FolderId, folderName: string) => void;
  /** the length of the entire folder path */
  pathLength: number;
};

export const SingleFolder = ({ data, folderIndex, nameOverride, onSelect, pathLength }: Props) => {
  const isEllipsis = pathLength > 3 && folderIndex === 1;
  const isLastFolder = folderIndex === pathLength - 1;
  const isRoot = folderIndex === 0;
  const [showTip, setShowTip] = React.useState(false);
  const folderName = nameOverride || data.name;

  if (pathLength > 3 && !isRoot && !isEllipsis && folderIndex < pathLength - 2) return null;

  return (
    <SegmentWrapper data-test="single-folder" isRoot={isRoot}>
      {!isRoot && <ChevronRightSmall />}
      {!isRoot && !isEllipsis && isLastFolder && <FolderOpen height={16} width={16} />}
      {!isRoot && !isEllipsis && !isLastFolder && <FolderClosed height={16} width={16} />}
      {isEllipsis ? (
        <StyledEllipsis>{'...'}</StyledEllipsis>
      ) : (
        <>
          <Tooltip isEnabled={showTip} tooltipContent={folderName}>
            <FolderName
              handleOverflow={setShowTip}
              id={data.id}
              isLastFolder={isLastFolder}
              name={folderName}
              onSelect={onSelect}
            />
          </Tooltip>
        </>
      )}
    </SegmentWrapper>
  );
};
